import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ShareButton from "@/components/ShareButton";
import { Calendar, ArrowLeft, X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://qbcron.com';
const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [galleryImages, setGalleryImages] = useState([]);
    const openLightbox = (index) => {
        setCurrentImageIndex(index);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
    };
    const closeLightbox = () => {
        setLightboxOpen(false);
        document.body.style.overflow = 'unset';
    };
    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    };
    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!lightboxOpen)
                return;
            if (e.key === 'Escape')
                closeLightbox();
            if (e.key === 'ArrowRight')
                nextImage();
            if (e.key === 'ArrowLeft')
                prevImage();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxOpen, galleryImages.length]);
    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await fetch(`${API_URL}/blog/${slug}`);
                if (!response.ok)
                    throw new Error('Post not found');
                const data = await response.json();
                setPost(data);
                // Update meta tags for social sharing
                if (data) {
                    document.title = `${data.title} - Qbcron Blog`;
                    // Construct full image URL with domain
                    let imageUrl = data.image;
                    if (!imageUrl.startsWith('http')) {
                        // Use full domain URL for absolute paths
                        imageUrl = `https://qbcron.com${data.image}`;
                    }
                    // Detect image type from URL
                    const getImageType = (url) => {
                        const ext = url.split('.').pop()?.toLowerCase();
                        const typeMap = {
                            'jpg': 'image/jpeg',
                            'jpeg': 'image/jpeg',
                            'png': 'image/png',
                            'gif': 'image/gif',
                            'webp': 'image/webp',
                            'avif': 'image/avif',
                            'svg': 'image/svg+xml',
                            'ogg': 'video/ogg',
                            'ogv': 'video/ogg'
                        };
                        return typeMap[ext || ''] || 'image/jpeg';
                    };
                    // Update or create OG meta tags
                    updateMetaTag('og:title', data.title);
                    updateMetaTag('og:description', data.excerpt);
                    updateMetaTag('og:image', imageUrl);
                    updateMetaTag('og:image:width', '1200');
                    updateMetaTag('og:image:height', '630');
                    updateMetaTag('og:image:type', getImageType(imageUrl));
                    updateMetaTag('og:url', window.location.href);
                    updateMetaTag('og:type', 'article');
                    updateMetaTag('og:site_name', 'Qbcron');
                    updateMetaTag('fb:app_id', '1262715482361493');
                    // Twitter cards
                    updateMetaTag('twitter:card', 'summary_large_image');
                    updateMetaTag('twitter:title', data.title);
                    updateMetaTag('twitter:description', data.excerpt);
                    updateMetaTag('twitter:image', imageUrl);
                }
            }
            catch (error) {
                console.error('Failed to fetch blog post:', error);
            }
            finally {
                setLoading(false);
            }
        };
        if (slug) {
            fetchPost();
        }
    }, [slug]);
    const updateMetaTag = (property, content) => {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
            element = document.querySelector(`meta[name="${property}"]`);
        }
        if (element) {
            element.setAttribute('content', content);
        }
        else {
            const meta = document.createElement('meta');
            if (property.startsWith('og:')) {
                meta.setAttribute('property', property);
            }
            else {
                meta.setAttribute('name', property);
            }
            meta.setAttribute('content', content);
            document.head.appendChild(meta);
        }
    };
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('el-GR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    if (loading) {
        return (_jsxs("div", { className: "min-h-screen", children: [_jsx(Navigation, {}), _jsxs("div", { className: "container mx-auto px-4 py-32 text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" }), _jsx("p", { className: "mt-4 text-muted-foreground", children: "Loading..." })] }), _jsx(Footer, {})] }));
    }
    if (!post) {
        return (_jsxs("div", { className: "min-h-screen", children: [_jsx(Navigation, {}), _jsxs("div", { className: "container mx-auto px-4 py-32 text-center", children: [_jsx("h1", { className: "text-4xl font-bold mb-4", children: "Post Not Found" }), _jsx("p", { className: "text-muted-foreground mb-8", children: "The blog post you're looking for doesn't exist." }), _jsx(Link, { to: "/blog", children: _jsxs(Button, { children: [_jsx(ArrowLeft, { className: "mr-2 h-4 w-4" }), "Back to Blog"] }) })] }), _jsx(Footer, {})] }));
    }
    return (_jsxs("div", { className: "min-h-screen", children: [_jsx(Navigation, {}), post.image && (_jsxs("div", { className: "relative h-96 md:h-[500px] overflow-hidden", children: [_jsx("img", { src: post.image.startsWith('http') ? post.image : `${BASE_URL}${post.image}`, alt: post.title, className: "w-full h-full object-cover" }), _jsx("div", { className: "absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" })] })), _jsx("article", { className: "container mx-auto px-4 -mt-32 relative z-10 pb-20", children: _jsxs("div", { className: "max-w-4xl mx-auto", children: [_jsxs("div", { className: "bg-card rounded-lg border border-border shadow-lg p-8 mb-8", children: [_jsx(Link, { to: "/blog", children: _jsxs(Button, { variant: "ghost", className: "mb-4 -ml-3", children: [_jsx(ArrowLeft, { className: "mr-2 h-4 w-4" }), "Back to Blog"] }) }), _jsx("div", { className: "flex items-center gap-4 text-muted-foreground mb-4", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Calendar, { size: 18 }), _jsx("span", { children: formatDate(post.createdAt) })] }) }), _jsx("h1", { className: "text-4xl md:text-5xl font-bold mb-6 text-glow-accent", children: post.title }), _jsx("p", { className: "text-xl text-muted-foreground leading-relaxed", children: post.excerpt }), _jsx("div", { className: "mt-6", children: _jsx(ShareButton, { url: window.location.href, title: post.title, description: post.excerpt }) })] }), _jsx("div", { className: "bg-card rounded-lg border border-border shadow-lg p-8 mb-8", children: _jsx("div", { className: "prose prose-lg max-w-none dark:prose-invert prose-headings:text-white", children: _jsx("div", { className: "leading-relaxed text-muted-foreground", dangerouslySetInnerHTML: { __html: post.content } }) }) }), post.images && (() => {
                            try {
                                const imageArray = JSON.parse(post.images);
                                if (imageArray.length > 0) {
                                    const fullImageUrls = imageArray.map((img) => img.startsWith('http') ? img : `${BASE_URL}${img}`);
                                    if (galleryImages.length === 0) {
                                        setGalleryImages(fullImageUrls);
                                    }
                                    return (_jsxs("div", { className: "bg-card rounded-lg border border-border shadow-lg p-8 mb-8", children: [_jsx("h2", { className: "text-2xl font-bold mb-4", children: "Article Gallery" }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: imageArray.map((img, i) => (_jsxs("div", { className: "group relative overflow-hidden rounded-lg border border-border hover:border-accent transition-colors cursor-pointer", onClick: () => openLightbox(i), children: [_jsx("img", { src: img.startsWith('http') ? img : `${BASE_URL}${img}`, alt: `${post.title} image ${i + 1}`, className: "w-full h-auto group-hover:scale-105 transition-transform duration-300" }), _jsx("div", { className: "absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center", children: _jsx(ExternalLink, { className: "text-white opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8" }) })] }, i))) })] }));
                                }
                            }
                            catch (e) {
                                console.error('Error parsing gallery images:', e);
                                return null;
                            }
                            return null;
                        })()] }) }), lightboxOpen && galleryImages.length > 0 && (_jsxs("div", { className: "fixed inset-0 z-50 bg-black/95 flex items-center justify-center", onClick: closeLightbox, children: [_jsx("button", { onClick: closeLightbox, className: "absolute top-4 right-4 text-white hover:text-accent transition-colors z-50", "aria-label": "Close lightbox", children: _jsx(X, { className: "h-8 w-8" }) }), _jsxs("div", { className: "absolute top-4 left-1/2 transform -translate-x-1/2 text-white text-lg font-medium z-50", children: [currentImageIndex + 1, " / ", galleryImages.length] }), galleryImages.length > 1 && (_jsx("button", { onClick: (e) => {
                            e.stopPropagation();
                            prevImage();
                        }, className: "absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-accent transition-colors z-50 bg-black/50 rounded-full p-3 hover:bg-black/70", "aria-label": "Previous image", children: _jsx(ChevronLeft, { className: "h-8 w-8" }) })), _jsx("div", { className: "max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4", onClick: (e) => e.stopPropagation(), children: _jsx("img", { src: galleryImages[currentImageIndex], alt: `Gallery image ${currentImageIndex + 1}`, className: "max-w-full max-h-full object-contain rounded-lg" }) }), galleryImages.length > 1 && (_jsx("button", { onClick: (e) => {
                            e.stopPropagation();
                            nextImage();
                        }, className: "absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-accent transition-colors z-50 bg-black/50 rounded-full p-3 hover:bg-black/70", "aria-label": "Next image", children: _jsx(ChevronRight, { className: "h-8 w-8" }) })), galleryImages.length > 1 && (_jsx("div", { className: "absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4", children: galleryImages.map((img, index) => (_jsx("button", { onClick: (e) => {
                                e.stopPropagation();
                                setCurrentImageIndex(index);
                            }, className: `flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all ${index === currentImageIndex
                                ? 'border-accent scale-110'
                                : 'border-white/30 hover:border-white/60'}`, children: _jsx("img", { src: img, alt: `Thumbnail ${index + 1}`, className: "w-full h-full object-cover" }) }, index))) }))] })), _jsx(Footer, {})] }));
};
export default BlogPost;
